import {
  Breadcrumbs,
  Inline,
  Notification,
  notifyError,
  notifySuccess,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useDocuments, useEmployee } from '@omnipresentgroup/domains';
import { DocumentForm } from '@omnipresentgroup/features';
import { DocumentAction } from '@omnipresentgroup/types';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { environment } from '../../../../environment';
import {
  EMPLOYEE_DETAILS_PATH,
  EMPLOYEE_DOCUMENTS_PATH,
} from '../../../constants';
import { useManagerUser } from '../../../hooks';

const EMPLOYEE_DETAILS_TITLE = 'Employee details';
const DOCUMENTS_TITLE = 'Documents';
const EMPLOYEE_ADD_DOCUMENT_PAGE_TITLE = 'Add document';
const SUCCESS_MESSAGE = 'The document has been successfully uploaded';
const ERROR_MESSAGE = 'The document could not be uploaded';

const fullNameRenderer = (firstName: string, lastName: string) =>
  `${firstName} ${lastName}`;

export const EmployeeAddDocumentPage = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams();

  const { companyId } = useManagerUser();

  const { data: employee } = useEmployee({
    id: employeeId,
    enabled: !!employeeId,
  });

  const { data: documentsInfo } = useDocuments({
    userId: employee?.userId || '',
    enabled: Boolean(employee?.userId || ''),
  });

  const cannotAddDocuments = useMemo(
    () =>
      !!documentsInfo?.actions &&
      !documentsInfo?.actions.includes(DocumentAction.CREATE),
    [documentsInfo],
  );

  const PageHeader = () => (
    <Stack gap="0">
      <Inline justify="space-between" minH={40} align="center">
        <Inline gap="8" align="center">
          <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
            {EMPLOYEE_ADD_DOCUMENT_PAGE_TITLE}
          </Typography>
        </Inline>
      </Inline>
      <Typography as="span" size="14" color="subtle">
        {employee &&
          fullNameRenderer(
            employee?.personalDetails.firstName,
            employee?.personalDetails.lastName,
          )}
      </Typography>
    </Stack>
  );

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'employee-details',
              title: `${EMPLOYEE_DETAILS_TITLE} | ${
                (employee?.personalDetails &&
                  fullNameRenderer(
                    employee.personalDetails.firstName,
                    employee.personalDetails.lastName,
                  )) ??
                ''
              }`,
              onClick: () => {
                window.location.href = `${
                  environment.apps.OMNIPLATFORM
                }${EMPLOYEE_DETAILS_PATH(employeeId)}`;
              },
            },
            {
              id: 'employee-documents',
              title: DOCUMENTS_TITLE,
              onClick: () => navigate(EMPLOYEE_DOCUMENTS_PATH(employeeId)),
            },
            {
              id: 'employee-add-document',
              title: EMPLOYEE_ADD_DOCUMENT_PAGE_TITLE,
              active: true,
            },
          ]}
        />

        <PageHeader />
      </Stack>

      {cannotAddDocuments && (
        <Notification
          id="add-document-notification"
          title="You do not have permission to add documents"
          intent="warning"
        />
      )}

      {employeeId && (
        <DocumentForm
          userId={employee?.userId}
          companyId={companyId}
          readonly={cannotAddDocuments}
          onSuccess={() => {
            notifySuccess({
              title: SUCCESS_MESSAGE,
            });
            navigate(EMPLOYEE_DOCUMENTS_PATH(employeeId));
          }}
          onError={(message) => {
            notifyError({
              title: ERROR_MESSAGE,
              description: message,
            });
          }}
        />
      )}
    </>
  );
};
